// apps/client/src/utils/dataProcessing.ts (CORRECTED)

import { SourceFile, Template, Office } from '../types';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveTemplateDisplayName } from './templateDisplayName';

type OrganizedFiles = Record<string, Record<string, Record<string, Record<string, SourceFile[]>>>>;

/**
 * Parse date from filename for file organization display.
 *
 * TEMPLATE-SPECIFIC DATE PATTERNS:
 * Each pattern is documented with which file template(s) it supports.
 * When adding new patterns, document the template and test existing patterns still work.
 *
 * @param fileName - The filename to parse
 * @returns Date object or null if no pattern matches
 */
function parseDateFromFileName(fileName: string): Date | null {
  const datePatterns = [
    // ==========================================================================
    // PATTERN 1: Valutten Standard Upload Format
    // Template: ALL (Client Upload Page auto-generates this format)
    // Example: "RUN 3 - October 2025.xlsx" → October 2025
    // ==========================================================================
    /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i,

    // ==========================================================================
    // PATTERN 2: Loan Market / AFG Aggregator Format
    // Template: Loan Market (LM), AFG
    // Example: "ENTO100 AGMT RUN 3 Nov 24.xlsx" → November 2024
    // Example: "ENTO100 AGMT RUN 1 Oct 25.xlsx" → October 2025
    // Note: Month abbreviation + 2-digit year, NO day number
    // ==========================================================================
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{2})(?:\.|\s|$)/i,

    // ==========================================================================
    // PATTERN 3: Entical Template Format
    // Template: Entical
    // Example: "Entical_Plan_T3_20250403.xlsx" → April 3, 2025
    // Note: YYYYMMDD format embedded in filename
    // ==========================================================================
    /(\d{4})(\d{2})(\d{2})(?:\D|$)/,

    // ==========================================================================
    // PATTERN 4: Standard Day-Month-Year Format
    // Template: Generic/Legacy
    // Example: "1 Mar 25" or "01-Mar-25" → March 1, 2025
    // ==========================================================================
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{2})/i,

    // ==========================================================================
    // PATTERN 5: ISO Date Format
    // Template: Generic/API exports
    // Example: "2025-04-03" or "2025/04/03" → April 3, 2025
    // ==========================================================================
    /(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})/,
  ];

  const monthMap: Record<string, number> = {
    'jan': 0, 'january': 0,
    'feb': 1, 'february': 1,
    'mar': 2, 'march': 2,
    'apr': 3, 'april': 3,
    'may': 4,
    'jun': 5, 'june': 5,
    'jul': 6, 'july': 6,
    'aug': 7, 'august': 7,
    'sep': 8, 'sept': 8, 'september': 8,
    'oct': 9, 'october': 9,
    'nov': 10, 'november': 10,
    'dec': 11, 'december': 11
  };

  for (let i = 0; i < datePatterns.length; i++) {
    const pattern = datePatterns[i];
    const match = fileName.match(pattern);
    if (!match) continue;

    // PATTERN 1: Valutten Standard - "October 2025" → month + year (1st of month)
    // Template: ALL
    if (i === 0) {
      const month = monthMap[match[1].toLowerCase()];
      const year = parseInt(match[2]);
      return new Date(year, month, 1);
    }

    // PATTERN 2: Loan Market/AFG - "Nov 24" → month + 2-digit year (1st of month)
    // Template: Loan Market, AFG
    if (i === 1) {
      const month = monthMap[match[1].toLowerCase()];
      const yearShort = parseInt(match[2]);
      const year = yearShort < 70 ? 2000 + yearShort : 1900 + yearShort;
      return new Date(year, month, 1);
    }

    // PATTERN 3: Entical - "20250403" → YYYYMMDD
    // Template: Entical
    if (i === 2) {
      const year = parseInt(match[1]);
      const month = parseInt(match[2]) - 1; // Zero-indexed
      const day = parseInt(match[3]);
      return new Date(year, month, day);
    }

    // PATTERN 4: Standard - "1 Mar 25" → day + month + year
    // Template: Generic/Legacy
    if (i === 3) {
      const day = parseInt(match[1]);
      const month = monthMap[match[2].toLowerCase()];
      const yearShort = parseInt(match[3]);
      const year = yearShort < 70 ? 2000 + yearShort : 1900 + yearShort;
      return new Date(year, month, day);
    }

    // PATTERN 5: ISO - "2025-04-03"
    // Template: Generic/API exports
    return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
  }

  return null;
}

/**
 * Extract every entity-code anchor an uploaded file carries, upper-cased.
 *
 * Used only to disambiguate when one office owns several loan books on the same
 * file template (Geelong). Post-flip uploads and re-tagged records expose the code
 * in one or more of: an explicit `entityCode`, the paired `rctiMetadata.entityCode`,
 * the `logicalKeyVariant` prefix (e.g. "ENTG100:AGMT"), or the filename's leading
 * token (e.g. "ENTG100 AGMT RUN 1 Jul 26.xlsx"). Standard Valutten filenames
 * ("RUN 2 - April 2021.xlsx") yield only the non-code token "RUN", which matches no
 * book and is harmless.
 */
function extractFileEntityCodes(file: SourceFile): Set<string> {
    const fileAny = file as any;
    const anchors = new Set<string>();
    const add = (value: unknown) => {
        if (typeof value === 'string' && value.trim()) anchors.add(value.trim().toUpperCase());
    };

    add(fileAny.entityCode);
    add(fileAny.rctiMetadata?.entityCode);
    if (typeof fileAny.logicalKeyVariant === 'string') {
        add(fileAny.logicalKeyVariant.split(':')[0]);
    }
    if (typeof file.fileName === 'string') {
        add(file.fileName.trim().split(/[\s_]+/)[0]);
    }
    return anchors;
}

interface EntityGroupMaps {
    entityBooksByTemplateOffice: Map<string, ResolvedLoanBook[]>;
    entityNameMap: Map<string, string>;
    templateToEntityCode: Map<string, string>;
    templateMap: Map<string, string>;
}

/**
 * Resolve the accordion group label for an uploaded file in a UNIFIED (entity) tenant.
 *
 * Primary key is (templateId + officeId): each office tab already scopes its files, so
 * a template SHARED across offices resolves to the book owned by the tab's office. When
 * exactly one book owns that pair we use it directly. When one office owns several books
 * on the same template (Geelong) the file's own entity code breaks the tie; absent any
 * code we emit a combined label rather than guess a single wrong book. Files with no
 * office-scoped entity book (missing officeId, non-entity template) fall back to the prior
 * templateId-based resolution so nothing regresses.
 */
function resolveEntityGroupName(file: SourceFile, maps: EntityGroupMaps): string {
    const { entityBooksByTemplateOffice, entityNameMap, templateToEntityCode, templateMap } = maps;
    const fileAny = file as any;
    const templateId = file.templateId;

    const officeBooks = (templateId && file.officeId)
        ? entityBooksByTemplateOffice.get(`${templateId}__${file.officeId}`) || []
        : [];

    if (officeBooks.length === 1) {
        return officeBooks[0].name;
    }
    if (officeBooks.length > 1) {
        const anchors = extractFileEntityCodes(file);
        const match = officeBooks.find(lb =>
            [lb.entityCode, ...(lb.entityCodes || [])]
                .some(code => code && anchors.has(code.toUpperCase()))
        );
        if (match) return match.name;
        // Ambiguous and un-anchored: preserve office order (loanBooks arrive sorted by
        // displayOrder) and label with every candidate rather than pick a wrong one.
        return officeBooks.map(lb => lb.name).join(' / ');
    }

    // No office-scoped entity book: keep the legacy resolution chain byte-identical.
    if (fileAny.entityCode && entityNameMap.has(fileAny.entityCode)) {
        return entityNameMap.get(fileAny.entityCode)!;
    }
    if (templateId && templateToEntityCode.has(templateId)) {
        const derivedEntityCode = templateToEntityCode.get(templateId)!;
        return entityNameMap.get(derivedEntityCode)
            || templateMap.get(templateId)
            || 'Uncategorized';
    }
    return templateId ? templateMap.get(templateId) || 'Uncategorized' : 'Uncategorized';
}

export const organizeFilesByOfficeTemplateYearMonth = (files: SourceFile[], templates: Template[], offices: Office[], loanBooks?: ResolvedLoanBook[]): OrganizedFiles => {
    const organized: OrganizedFiles = {};
    // Factory-created templates store `name`, admin-portal-created ones store
    // `templateName`; reading only the latter rendered an "Unknown Template"
    // group for every factory-seeded tenant.
    const templateMap = new Map(templates.map(t => [t.id, resolveTemplateDisplayName(t)]));
    const officeMap = new Map(offices.map(o => [o.id, o.name || 'Unknown Office']));

    // For unified-mode companies: map entityCode → loan book name
    const hasEntityLoanBooks = loanBooks?.some(lb => lb.type === 'entity') || false;
    const entityNameMap = new Map(
        (loanBooks || []).filter(lb => lb.type === 'entity' && lb.entityCode)
            .map(lb => [lb.entityCode!, lb.name])
    );
    // Fallback resolver: derive entityCode from templateId for legacy files
    // uploaded before entity_mappings were seeded (their SourceFile docs lack
    // file.entityCode but the loan_book → template mapping can still resolve them).
    // This prevents the same template from rendering as two separate accordion
    // groups (e.g., "LMG Multi-Sheet" + "Loan Market (LMG)").
    const templateToEntityCode = new Map(
        (loanBooks || []).filter(lb => lb.type === 'entity' && lb.entityCode && lb.templateId)
            .map(lb => [lb.templateId!, lb.entityCode!])
    );
    // Primary resolver for unified tenants: entity books grouped by (templateId + officeId).
    // A single file template can be SHARED by many office-specific books post-flip (e.g.
    // Entourage's Loan Market "RIS…" template maps to six books, one per office). The file's
    // office tab already scopes it, so the correct book is the one whose office matches the
    // tab, NOT an arbitrary last-write-wins pick from templateToEntityCode. Where one office
    // owns several books on the same template (Geelong: Loan Market Geelong / Entourage
    // Geelong / Entlong) the per-upload entity code breaks the tie (see resolveEntityGroupName).
    const entityBooksByTemplateOffice = new Map<string, ResolvedLoanBook[]>();
    for (const lb of (loanBooks || [])) {
        if (lb.type !== 'entity' || !lb.templateId || !lb.officeId) continue;
        const key = `${lb.templateId}__${lb.officeId}`;
        const existing = entityBooksByTemplateOffice.get(key);
        if (existing) existing.push(lb);
        else entityBooksByTemplateOffice.set(key, [lb]);
    }

    // Month name to number mapping for periodMonth field
    const monthNameToNum: Record<string, number> = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3,
      'may': 4, 'june': 5, 'july': 6, 'august': 7,
      'september': 8, 'october': 9, 'november': 10, 'december': 11
    };

    files.forEach(file => {
        const officeName = file.officeId ? officeMap.get(file.officeId) || 'Uncategorized' : 'Uncategorized';

        // For entity-based companies: group by entity book name instead of template name.
        const fileAny = file as any;
        let groupName: string;
        if (hasEntityLoanBooks) {
            groupName = resolveEntityGroupName(file, {
                entityBooksByTemplateOffice,
                entityNameMap,
                templateToEntityCode,
                templateMap,
            });
        } else {
            groupName = file.templateId ? templateMap.get(file.templateId) || 'Uncategorized' : 'Uncategorized';
        }

        let year: string;
        let monthName: string;
        let monthNum: number;

        // PRIORITY 1: Use periodMonth from upload_intent if in YYYY-MM format (most reliable)
        const periodMonth = fileAny.periodMonth;
        const isoMatch = periodMonth?.match(/^(\d{4})-(\d{2})$/);

        if (isoMatch) {
            // ISO format: "2024-11" -> year=2024, monthNum=10 (0-indexed), monthName=November
            year = isoMatch[1];
            monthNum = parseInt(isoMatch[2], 10) - 1;  // Convert to 0-indexed
            monthName = new Date(parseInt(year), monthNum, 1).toLocaleString('default', { month: 'long' });
        } else {
            // PRIORITY 2: Parse from fileName (handles legacy uploads with wrong periodMonth format)
            // This fixes the "2000 year" bug for files with periodMonth: "November" instead of "2024-11"
            let date = parseDateFromFileName(file.fileName || '');

            // PRIORITY 3: Fall back to uploadTimestamp
            if (!date || isNaN(date.getTime())) {
                date = file.uploadTimestamp?.toDate ? file.uploadTimestamp.toDate() : new Date();
            }

            year = date.getFullYear().toString();
            monthName = date.toLocaleString('default', { month: 'long' });
            monthNum = date.getMonth();
        }

        const monthKey = `${String(monthNum).padStart(2, '0')}_${monthName}`;

        if (!organized[officeName]) organized[officeName] = {};
        if (!organized[officeName][groupName]) organized[officeName][groupName] = {};
        if (!organized[officeName][groupName][year]) organized[officeName][groupName][year] = {};
        if (!organized[officeName][groupName][year][monthKey]) organized[officeName][groupName][year][monthKey] = [];

        organized[officeName][groupName][year][monthKey].push(file);
    });
    
    // Sort months and files within each month
    for (const office in organized) {
        for (const template in organized[office]) {
            for (const year in organized[office][template]) {
                const sortedMonths = Object.keys(organized[office][template][year]).sort((a: any, b: any) => b.localeCompare(a));
                const sortedMonthData: Record<string, SourceFile[]> = {};
                for (const monthKey of sortedMonths) {
                    // Sort files within each month by run number (descending) and upload date (descending)
                    const sortedFiles = organized[office][template][year][monthKey].sort((a, b) => {
                        // Extract run number from filename (e.g., "RUN 3 - August 2025.xlsx" -> 3).
                        // Fall back to displayName when the raw filename carries no "RUN N"
                        // (e.g. Choice/Peake files are "…(36).XLS"; the run lives only in the
                        // displayed label). Zero blast radius: templates whose filename DOES
                        // carry "RUN" (Entourage "ENTO100 AGMT RUN 3 …") keep using it.
                        const runA = extractRunNumber(a.fileName || '') || extractRunNumber((a as any).displayName || '');
                        const runB = extractRunNumber(b.fileName || '') || extractRunNumber((b as any).displayName || '');

                        // Sort by run number descending (RUN 3 before RUN 1) — DO NOT CHANGE direction.
                        if (runA !== runB) {
                            return runB - runA;
                        }

                        // Tiebreaker: sort by upload date descending (newest first)
                        const dateA = a.uploadTimestamp?.toDate?.() || new Date(0);
                        const dateB = b.uploadTimestamp?.toDate?.() || new Date(0);
                        return dateB.getTime() - dateA.getTime();
                    });
                    sortedMonthData[monthKey] = sortedFiles;
                }
                organized[office][template][year] = sortedMonthData;
            }
        }
    }
    return organized;
};

/**
 * Extract run number from filename like "RUN 3 - August 2025.xlsx" -> 3
 * Returns 0 if no run number found (will sort to bottom)
 */
function extractRunNumber(fileName: string): number {
    const match = fileName.match(/RUN\s+(\d+)/i);
    return match ? parseInt(match[1]) : 0;
}

export function normalizeBrokerName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '';
  
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function getBrokerDisplayName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '';
  
  const cleaned = name.trim().replace(/\s+/g, ' ');
  
  // Convert to Title Case for UI display
  return cleaned
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      if (word.startsWith('(')) {
        return '(' + word.charAt(1).toUpperCase() + word.slice(2);
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Checks if two broker names should be considered the same broker
 */
export function isSameBroker(name1: string, name2: string): boolean {
  return normalizeBrokerName(name1) === normalizeBrokerName(name2);
}

/**
 * Extracts base name without parenthetical qualifiers
 * Used only for UI grouping, NOT for identity matching
 */
export function getBrokerBaseName(name: string): string {
  if (!name) return '';
  return name.replace(/\s*\([^)]*\).*$/, '').trim();
}